import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useBanners } from '../../hooks/useBanners';
import { safeImageUrl } from '../../utils/url';
import { HomeBanner } from '../../models';

const AUTO_ADVANCE_INTERVAL_MS = 6000;

interface HomeBannersProps {
  onSelectCollection: (collectionId: number) => void;
}

function HomeBannersSkeleton() {
  return <div className="aspect-[16/10] sm:aspect-[21/9] rounded-2xl bg-champagne animate-pulse" />;
}

function BannerImage({ banner }: { banner: HomeBanner }) {
  return (
    <img
      src={safeImageUrl(banner.imageUrl ?? '')}
      alt=""
      draggable={false}
      className="w-full h-full object-cover"
    />
  );
}

function BannerSlide({ banner, onSelectCollection }: { banner: HomeBanner; onSelectCollection: (id: number) => void }) {
  if (banner.linkType === 1 && banner.linkTargetId) {
    return (
      <Link to={`/produtos/${banner.linkTargetId}`} className="block w-full h-full">
        <BannerImage banner={banner} />
      </Link>
    );
  }

  if (banner.linkType === 2 && banner.linkTargetId) {
    return (
      <button
        type="button"
        onClick={() => onSelectCollection(banner.linkTargetId as number)}
        className="block w-full h-full text-left"
      >
        <BannerImage banner={banner} />
      </button>
    );
  }

  if (banner.linkType === 3 && banner.linkUrl) {
    return (
      <a href={banner.linkUrl} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
        <BannerImage banner={banner} />
      </a>
    );
  }

  return (
    <div className="w-full h-full">
      <BannerImage banner={banner} />
    </div>
  );
}

export function HomeBanners({ onSelectCollection }: HomeBannersProps) {
  const { banners, isLoading, error, isEmpty } = useBanners();
  const [currentIndex, setCurrentIndex] = useState(0);
  const isPausedRef = useRef(false);

  const hasMultiple = banners.length > 1;

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? banners.length - 1 : prev - 1));
  }, [banners.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === banners.length - 1 ? 0 : prev + 1));
  }, [banners.length]);

  useEffect(() => {
    if (!hasMultiple) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const id = window.setInterval(() => {
      if (!isPausedRef.current) goToNext();
    }, AUTO_ADVANCE_INTERVAL_MS);

    return () => window.clearInterval(id);
  }, [hasMultiple, goToNext]);

  if (error || isEmpty) return null;

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 pt-10">
      {isLoading ? (
        <HomeBannersSkeleton />
      ) : (
        <div
          className="relative rounded-2xl overflow-hidden shadow-sm aspect-[16/10] sm:aspect-[21/9] bg-champagne-light"
          onMouseEnter={() => { isPausedRef.current = true; }}
          onMouseLeave={() => { isPausedRef.current = false; }}
        >
          <div
            className="flex w-full h-full transition-transform duration-700 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {banners.map((banner) => (
              <div key={banner.id} className="w-full h-full shrink-0">
                <BannerSlide banner={banner} onSelectCollection={onSelectCollection} />
              </div>
            ))}
          </div>

          {hasMultiple && (
            <>
              <button
                type="button"
                onClick={goToPrev}
                aria-label="Banner anterior"
                className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-text-dark flex items-center justify-center shadow-sm transition-opacity"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={goToNext}
                aria-label="Próximo banner"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-text-dark flex items-center justify-center shadow-sm transition-opacity"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                {banners.map((b, idx) => (
                  <span
                    key={b.id}
                    className={`h-1.5 rounded-full transition-all duration-200 ${
                      idx === currentIndex ? 'w-3.5 bg-white' : 'w-1.5 bg-white/55'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
